import { FormRules } from "element-plus";
import { nextTick, reactive, Ref } from "vue";
import { emitter } from "@/plugins/event-bus";
import { useStore } from "@/store";
import { DxDataGridInitializedEvent } from "@/models/dx-types";

const store = useStore();
const registeredGrids = reactive<DxDataGridInitializedEvent[]>([]);

export interface PropertyState {
  [name: string]: boolean;
}

export interface ComponentHelperMixins {
  createValidationState(rules: FormRules | undefined): PropertyState;
  isValidationStateValid(state: PropertyState): boolean;
  handleValidationCurrentState(validationState: PropertyState, propName: string, isValid: boolean): void;
  setValidationCurrentState(validationState: PropertyState, propNames: string[], value: boolean): void;
  handleDialogCancel(dialogState: Ref<boolean>, id?: string): void;
  handleDialogCancelForced(): void;
  triggerFocus(delay?: number): void;
  deepEqual(entity1: any, entity2: any, ...ignoreFields: string[]): boolean;
  registerGrid(dxDataGridRef: DxDataGridInitializedEvent): void;
  triggerRepaintGrids(): void;
  triggerDatePickerEnableActions(): void;
}

export const componentHelperMixins: ComponentHelperMixins = {
  createValidationState(rules: FormRules) {
    return rules && reactive(Object.keys(rules).reduce((obj, cur) => ({ ...obj, [cur]: false }), {}));
  },
  isValidationStateValid(state: PropertyState) {
    return state ? Object.values(state).reduce((acc, next) => acc && next) : true;
  },
  handleValidationCurrentState(validationState: PropertyState, propName: string, isValid: boolean) {
    validationState[propName] != undefined && (validationState[propName] = isValid);
  },
  setValidationCurrentState(validationState: PropertyState, propNames: string[], value: boolean) {
    if (!validationState) return;

    propNames.forEach((propName) => {
      if (propName in validationState) {
        validationState[propName] = value;
      }
    });
  },
  handleDialogCancel(dialogState: Ref<boolean>, id?: string) {
    dialogState.value = false;
    privateUnsubscribeDialogEvents(id);
    /*dialogMixins
      .showConfirmDialog("Уведомление", `Вы действительно хотите закрыть окно ?`, "warning")
      .then((result) => {
        if (result.isConfirmed) {
          dialogState.value = false;
          privateUnsubscribeDialogEvents();
        }
      });*/
  },
  handleDialogCancelForced() {
    privateUnsubscribeDialogEvents();
  },
  triggerFocus(delay?: number) {
    setTimeout(privateFocus, delay ?? 500);
  },
  deepEqual(obj1: any, obj2: any, ...ignoreFields: string[]) {
    return privateDeepEqual(obj1, obj2, ...ignoreFields);
  },
  registerGrid(dxDataGridRef: any) {
    registeredGrids.push(dxDataGridRef);
  },
  triggerRepaintGrids() {
    nextTick().then(() => {
      registeredGrids.forEach((data) => {
        if (data.element.offsetParent) {
          data.component.instance().repaint();
        }
      });
    });
  },
  triggerDatePickerEnableActions() {
    nextTick().then(() => {
      document
        .querySelectorAll<HTMLButtonElement>(
          ".date-picker-single-panel .el-picker-panel__content .el-date-range-picker__header button[disabled]"
        )
        .forEach((x) => {
          x.disabled = false;
          x.classList.remove("is-disabled");
        });
    });
  },
};

const privateDeepEqual = (obj1: any, obj2: any, ...ignoreFields: string[]): boolean => {
  const ok = Object.keys;
  const tobj1 = typeof obj1;
  const tobj2 = typeof obj2;

  // Сравнение объектов Date
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // Остальная логика сравнения
  return obj1 && obj2 && tobj1 === "object" && tobj1 === tobj2
    ? ok(obj1).length === ok(obj2).length &&
        ok(obj1).every((key) =>
          ignoreFields.includes(key) ? true : privateDeepEqual(obj1[key], obj2[key], ...ignoreFields)
        )
    : obj1 === obj2;
};

const privateUnsubscribeDialogEvents = (id?: string) => {
  store.commit("clearSteps", undefined);
  emitter.off(id ? "dialogFormBack" + id : "dialogFormBack");
  emitter.off(id ? "dialogFormCancel" + id : "dialogFormCancel");
};

const privateFocus = () => {
  const elements = document.querySelectorAll<HTMLInputElement>("input[autofocus], [autofocus] input");
  elements.forEach((x) => x.offsetParent !== null && x.focus());
};

export default {
  methods: componentHelperMixins,
};
